import React from 'react';
import MetricTiles from '../../../../Elements/MetricTiles';
import CheckboxList from '../../../../Elements/InputComponents/BaseInputs/CheckboxList';
import CheckListToneEnum from '../../../../Enums/CheckListToneEnum';
import {
  formatCount,
  getOutOfScopeGroupKey,
  getOutOfScopeGroupLabel,
  getRunPreviewLead,
  getRunPreviewTiles,
} from '../../../../Helpers/invoiceAutomationRunHelper';
import InvoiceAutomationRunDrafts from './InvoiceAutomationRunDrafts';
import InvoiceAutomationReasonCounts from './InvoiceAutomationReasonCounts';
import InvoiceAutomationRemovedJobs from './InvoiceAutomationRemovedJobs';

// What Run Now is about to do, laid out as the mockup's "Try it out": the counts in tiles, a row for each draft the
// run would make or add to, the jobs it would take off and why, the out-of-scope jobs with an unticked box for each
// kind and each cancelled month, and why jobs cannot be added. Ticking a box brings those jobs into the run; the window then works the run out again
// (isUpdating) and the tiles and rows include them. Shows a waiting line while the first preview (null) is loading.
// When the run could not be worked out for the latest ticks (isOutdated), the previous answer stays and says so.
export default function InvoiceAutomationRunPreview({
  preview = null,
  isUpdating = false,
  isOutdated = false,
  tickedGroupKeys = [],
  onTickedGroupKeysChange = (newTickedGroupKeys) => {},
}) {
  if (preview === null) {
    return (
      <div className='iauto-run'>
        <p className='iauto-run-lead'>Working out what the run would do…</p>
      </div>
    );
  }

  const groupOptions = (preview.outOfScopeGroups ?? []).map((group) => ({
    value: getOutOfScopeGroupKey(group),
    label: (
      <span className='iauto-reason'>
        <span>{getOutOfScopeGroupLabel(group)}</span>
        <b className='iauto-reason-count'>{formatCount(group.jobCount, 'job', 'jobs')}</b>
      </span>
    ),
  }));
  const lead = getRunPreviewLead(isUpdating, isOutdated);
  return (
    <div className='iauto-run'>
      <p className='iauto-run-lead'>{lead}</p>
      <MetricTiles tiles={getRunPreviewTiles(preview)} />
      <InvoiceAutomationRunDrafts drafts={preview.drafts ?? []} />
      <InvoiceAutomationRemovedJobs
        title='Jobs to remove'
        lead='They no longer pass the Ready for Invoicing rules, so the run takes them off their drafts. Once they are ready again, a later run adds them back.'
        jobs={preview.jobsNoLongerReady ?? []}
      />
      {groupOptions.length > 0 && (
        <section className='iauto-run-section'>
          <h4>Out of scope</h4>
          <p>The run leaves these jobs out. Tick a line to bring its jobs into this run.</p>
          <CheckboxList
            options={groupOptions}
            selectedValues={tickedGroupKeys}
            onChange={onTickedGroupKeysChange}
          />
        </section>
      )}
      <InvoiceAutomationReasonCounts
        title='Why jobs cannot be added'
        reasons={preview.skipReasons ?? []}
        tone={CheckListToneEnum.Warn}
      />
    </div>
  );
}
